import styles from "./addExistingUserModalSecondStep.module.scss";
import { useTranslation } from "react-i18next";
import {
  array,
  boolean,
  date,
  number,
  object
} from "yup";
import {
  useFieldArray,
  useForm
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dropdown,
  DropdownItem
} from "../../../shared/components/dropdown/dropdown";
import { useMemo } from "react";
import {
  AssignUserToProject,
  UserToAssign
} from "../../../types/user.types";
import { useGetAllRolesQuery } from "../../../api/services/role.service";
import { Project } from "../../../types/project.types";
import { CheckboxHookForm } from "../../../shared/components/checkboxHookForm/checkboxHookForm";
import { DateInput } from "../../../shared/components/dateInput/dateInput";
import removeBlockIcon from "../../../shared/images/removeBlock.svg";

type AddExistingUserModalSecondStepProps = {
  selectedUsers: Array<UserToAssign>;
  onChangeSelectedUsers: (selectedUsers: Array<UserToAssign>) => void;
  project: Project;
  onCancelClick: () => void;
  onClickConfirm: (assignUsers: Array<AssignUserToProject>) => void;
}

export const AddExistingUserModalSecondStep = ({ selectedUsers, onChangeSelectedUsers, project, onCancelClick, onClickConfirm }: AddExistingUserModalSecondStepProps) => {
  const { t } = useTranslation();

  const { data: roles } = useGetAllRolesQuery("");

  const initialSchema = object({
    assignUsers: array().required().of(
      object().shape({
        userId: number().required(t("common.notValid")),
        passwordExpirationDate: date().required(),
        isActive: boolean().required(),
        roleId: number().required(t("common.notValid"))
      })
  )});

  const getDefaultDate = (): Date => {
    const passwordExpirationDate = new Date();
    passwordExpirationDate.setMonth(passwordExpirationDate.getMonth() + 6);
    return passwordExpirationDate;
  }

  const {
    control,
    handleSubmit
  } = useForm({
    resolver: yupResolver(initialSchema),
    mode: "onChange",
    defaultValues: { assignUsers: selectedUsers.map(item => ({
        userId: item.id,
        passwordExpirationDate: getDefaultDate(),
        isActive: true,
        roleId: 0
      })) }
  });
  const {
    fields: assignUsersFields,
    remove: removeAssignUsers
  } = useFieldArray({ control, name: "assignUsers" });


  const onConfirm = (): void => {
    handleSubmit((form) => {
      onClickConfirm(form.assignUsers.map(item => ({
        projectId: project.id,
        userId: item.userId,
        roleId: item.roleId,
        passwordExpirationDate: item.passwordExpirationDate.toISOString(),
        isActive: item.isActive
      })))
    })();
  }

  const onClickRemoveUser = (index: number): void => {
    const newSelectedUsers = [...selectedUsers];
    newSelectedUsers.splice(index, 1);
    onChangeSelectedUsers(newSelectedUsers);
    removeAssignUsers(index);
  }

  const rolesDropdown: DropdownItem[] = useMemo(() => {
    return roles?.filter(item => !item.isTestToolAdmin).map(item => ({
      id: item.id,
      name: item.name,
      title: item.name
    })) || [];
  }, [roles]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.label}>{t("main.project")}</div>
        <div className={styles.value}>{project.name}</div>
      </div>
      <div className={styles.users}>
        <div className={styles.title}>
          <div className={styles.name}>{t("login.name")}</div>
          <div className={styles.email}>{t("admin.email")}</div>
          <div className={styles.role}>{t("admin.role")}</div>
          <div className={styles.active}>{t("admin.active")}</div>
          <div className={styles.expirationDate}>{t("user.expirationDate")}</div>
        </div>
        {assignUsersFields.map((userField, index) => {
          return (
            <div className={styles.user} key={userField.id}>
              <div className={styles.name}>{selectedUsers[index].fullName}</div>
              <div className={styles.email}>{selectedUsers[index].email}</div>
              <div className={styles.input}>
                <Dropdown items={rolesDropdown}
                          name={`assignUsers.${index}.roleId`}
                          control={control}/>
              </div>
              <div className={styles.active}>
                <CheckboxHookForm control={control} name={`assignUsers.${index}.isActive`}/>
              </div>
              <div className={styles.input}>
                <DateInput control={control} name={`assignUsers.${index}.passwordExpirationDate`}/>
              </div>
              <div className={styles.action}>
                <img src={removeBlockIcon} alt="" onClick={() => onClickRemoveUser(index)}/>
              </div>
            </div>
          )
        })}
      </div>
      <div className={styles.buttons}>
        <button className={styles.cancel}
                onClick={onCancelClick}>{t("common.cancel")}</button>
        <button className={styles.continue}
                onClick={onConfirm}>{t("admin.confirm")}</button>
      </div>
    </div>
  );
}